import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { applyFiltersAndSort, convertToCSV, downloadCSV, transformToRightSwipes, transformToLeftSwipes } from './utils';
import { getDealerDashboardData, getLeftSwipeData, lookupInventoryByVins, getActiveMembersSummary } from '../../actions/Actions';
import { createLeftSwipesColumns, createRightSwipesColumns } from './tableColumns';
import { renderFilterDropdown, renderSortDropdown } from './FilterSortComponents';
import OverviewSection from './OverviewSection';
import LeftSwipesTable from './LeftSwipesTable';
import RightSwipesTable from './RightSwipesTable';
import DealDetailsModal from './DealDetailsModal';
import CreditUnionModal from './CreditUnionModal';
import { SWIPE_GESTURE_CONFIG } from './constants';
import './DealerDashboard.css';

// TODO: Remove this constant when login is restored
// Temporary hardcoded fp_dealer_id for testing
//const TEMP_FP_DEALER_ID = 14943;

const DealerDashboard = () => {
  const currentDealerhost = useSelector((state) => state.dealerHosts.current);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [salesforceData, setSalesforceData] = useState(null);
  const [realRightSwipesData, setRealRightSwipesData] = useState([]);
  const [realLeftSwipesData, setRealLeftSwipesData] = useState([]);
  
  const [sellerName, setSellerName] = useState('');
  const computedFpDealerId = currentDealerhost?.fp_dealer_id;
  const toTitleCase = (s) => {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };
  
  // Fetch Salesforce and FastPass data when fp_dealer_id is available
  useEffect(() => {
    const fetchDashboardData = async () => {
      // Get fp_dealer_id from Redux state or use temporary constant
      const fpDealerId = computedFpDealerId;
      
      if (!fpDealerId) {
        console.warn('No fp_dealer_id available');
        setRealRightSwipesData([]);
        setRealLeftSwipesData([]);
        return;
      }

      // Log source of fp_dealer_id
      if (currentDealerhost?.fp_dealer_id) {
        console.log('✅ Using fp_dealer_id from Redux:', fpDealerId);
        console.log('📍 Dealer:', currentDealerhost.dealer_nm);
      } else {
        console.log('⚠️ Using TEMP_FP_DEALER_ID:', fpDealerId, '(login not available)');
      }

      
      
      try {
        console.log('🔍 Fetching dashboard data for fp_dealer_id:', fpDealerId);
        
        // Fetch Salesforce data (Right Swipes)
        const salesforcePromise = getDealerDashboardData(fpDealerId);
        
        // Fetch FastPass data (Left Swipes)
        const leftSwipePromise = getLeftSwipeData(fpDealerId);
          
          // Wait for API calls
          const [salesforceResult, leftSwipeResult, summaryResult] = await Promise.allSettled([
            salesforcePromise,
            leftSwipePromise,
            getActiveMembersSummary()
          ]);
          
          

          // Handle Salesforce data (Right Swipes)
          if (salesforceResult.status === 'fulfilled') {
            const data = salesforceResult.value;
            console.log('Salesforce data received:', data);
            
            if (data && data.success && data.success.ActiveGarages) {
              setSalesforceData(data);
              
              // Transform active garages to right swipes format
              let transformedRightSwipes = transformToRightSwipes(
                data.success.ActiveGarages,
                data.success.ActiveDealerPros
              );

              // Enrich with inventory data from FastPass in one call
              try {
                const vins = Array.from(new Set(transformedRightSwipes.map(r => r.vin).filter(Boolean)));
                if (vins.length > 0) {
                  const items = await lookupInventoryByVins(vins);
                  const byVin = new Map(items.map(i => [String(i.vin).toUpperCase(), i]));
                  // capture seller_name once if present
                  const anySeller = items.find(i => i && i.seller_name && String(i.seller_name).trim() !== '');
                  if (anySeller && anySeller.seller_name) {
                    setSellerName(toTitleCase(anySeller.seller_name));
                  }
                  transformedRightSwipes = transformedRightSwipes.map(r => {
                    const inv = byVin.get(String(r.vin || '').toUpperCase());
                    if (!inv) return r;
                    const milesVal = inv.miles != null ? String(inv.miles) : '';
                    const milesNum = parseInt(String(milesVal).replace(/[^0-9]/g, ''), 10);
                    const conditionVal = Number.isFinite(milesNum) && milesNum < 500 ? 'New' : 'Used';
                    return {
                      ...r,
                      stock: inv.stock_no || r.stock,
                      msrp: inv.price != null ? `$${Number(inv.price).toLocaleString()}` : r.msrp,
                      priceNumber: inv.price != null ? Number(inv.price) : undefined,
                      miles: milesVal,
                      condition: conditionVal,
                      vehicle: inv.year && inv.make && inv.model
                        ? `${inv.year} ${inv.make} ${inv.model}`
                        : r.vehicle,
                      trim: inv.trim ? `${inv.trim} • ${inv.exterior_color || ''}`.trim() : r.trim,
                      fastpassVehicleId: inv.fastpassVehicleId || inv.row_id || r.fastpassVehicleId,
                      vehiclePhoto: inv.photo || inv.photo_url || r.vehiclePhoto || '',
                      inventoryRowId: inv.row_id || r.inventoryRowId
                    };
                  });
                }
              } catch (e) {
                console.warn('Inventory enrichment failed', e);
              }

              setRealRightSwipesData(transformedRightSwipes);
              
              
            } else {
              console.warn('No active garages data found');
            }
          } else {
            console.error('Error fetching Salesforce data:', salesforceResult.reason);
            setRealRightSwipesData([]);
          }
          
          // Handle FastPass data (Left Swipes)
          if (leftSwipeResult.status === 'fulfilled') {
            const data = leftSwipeResult.value;
            console.log('FastPass left swipe data received:', data);
            
            if (data && data.status && data.data && data.data.activities) {
              // Transform activities to left swipes format
              const transformedLeftSwipes = transformToLeftSwipes(data.data.activities);
              setRealLeftSwipesData(transformedLeftSwipes);
              
              // Success toast removed to avoid duplicate notifications in dev
            } else {
              console.warn('No left swipe activities found');
              message.warning('Some dashboard data is unavailable');
            }
          } else {
            console.error('Error fetching left swipe data:', leftSwipeResult.reason);
            setRealLeftSwipesData([]);
          }
          // Handle Active Members Summary (DB)
          if (summaryResult.status === 'fulfilled' && summaryResult.value && summaryResult.value.status) {
            const sum = summaryResult.value.data || {};
            if (typeof sum.activeMembers === 'number') setActiveMembersCountOverride(sum.activeMembers);
            if (typeof sum.totalOpportunity === 'number') setTotalOpportunityMOverride(sum.totalOpportunity / 1_000_000);
            if (Array.isArray(sum.breakdown)) setCreditUnionBreakdown(sum.breakdown);
          }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        message.error('Failed to load dashboard data.');
        setRealRightSwipesData([]);
        setRealLeftSwipesData([]);
      } finally {
        
      }
    };
    
    fetchDashboardData();
  }, [currentDealerhost, computedFpDealerId]);
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dealDetailsModal, setDealDetailsModal] = useState(false);
  const [showCreditUnionModal, setShowCreditUnionModal] = useState(false);
  const [creditUnionBreakdown, setCreditUnionBreakdown] = useState([]);
  const [activeMembersCountOverride, setActiveMembersCountOverride] = useState(null);
  const [totalOpportunityMOverride, setTotalOpportunityMOverride] = useState(null);
 
  const activeRightSwipesData = realRightSwipesData;
  const activeLeftSwipesData = realLeftSwipesData;
  
  // Dynamic pie chart data based on actual record counts
  const pieData = [
    { name: 'Right Swipes', value: activeRightSwipesData.length, color: '#52c41a' },
    { name: 'Left Swipes', value: activeLeftSwipesData.length, color: '#ff4d4f' }
  ];

  // Active members count: prefer DB summary override, fallback to Salesforce or 0
  const activeMembersCount = (activeMembersCountOverride != null)
    ? activeMembersCountOverride
    : (salesforceData?.success?.ActiveGarages?.length || 0);

  // State for filtered data
  const [filteredLeftData, setFilteredLeftData] = useState(activeLeftSwipesData);
  const [filteredRightData, setFilteredRightData] = useState(
    applyFiltersAndSort(activeRightSwipesData, {}, {}, false)
  );
  
  // Update filtered data when real data is loaded
  useEffect(() => {
    if (realRightSwipesData.length > 0) {
      setFilteredRightData(
        applyFiltersAndSort(realRightSwipesData, {}, {}, false)
      );
    }
  }, [realRightSwipesData]);
  
  useEffect(() => {
    if (realLeftSwipesData.length > 0) {
      setFilteredLeftData(realLeftSwipesData);
    }
  }, [realLeftSwipesData]);
  // Pagination state for Left Swipes
  const [leftCurrentPage, setLeftCurrentPage] = useState(1);
  const [leftPageSize, setLeftPageSize] = useState(10);
  // Pagination state for Right Swipes
  const [rightCurrentPage, setRightCurrentPage] = useState(1);
  const [rightPageSize, setRightPageSize] = useState(10);
  
  // State for column filters
  const [leftSwipesFilters, setLeftSwipesFilters] = useState({});
  const [rightSwipesFilters, setRightSwipesFilters] = useState({});
  const [leftSwipesSort, setLeftSwipesSort] = useState({});
  const [rightSwipesSort, setRightSwipesSort] = useState({});
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeIndicatorActive, setSwipeIndicatorActive] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  // Define handlers first
  const handleViewDealDetails = (record) => {
    setSelectedRecord(record);
    setDealDetailsModal(true);
  };

  const handleColumnFilter = (columnKey, value, isLeftSwipes = true) => {
    if (isLeftSwipes) {
      const newFilters = { ...leftSwipesFilters };
      if (value && value.length > 0) {
        newFilters[columnKey] = value;
      } else {
        delete newFilters[columnKey];
      }
      setLeftSwipesFilters(newFilters);
      const filtered = applyFiltersAndSort(activeLeftSwipesData, newFilters, leftSwipesSort, true);
      setFilteredLeftData(filtered);
      setLeftCurrentPage(1);
    } else {
      const newFilters = { ...rightSwipesFilters };
      if (value && value.length > 0) {
        newFilters[columnKey] = value;
      } else {
        delete newFilters[columnKey];
      }
      setRightSwipesFilters(newFilters);
      const filtered = applyFiltersAndSort(activeRightSwipesData, newFilters, rightSwipesSort, false);
      setFilteredRightData(filtered);
      setRightCurrentPage(1);
    }
  };

  const handleColumnSort = (columnKey, sortOrder, isLeftSwipes = true) => {
    if (isLeftSwipes) {
      const newSort = { ...leftSwipesSort };
      if (sortOrder) {
        newSort[columnKey] = sortOrder;
      } else {
        delete newSort[columnKey];
      }
      setLeftSwipesSort(newSort);
      const filtered = applyFiltersAndSort(activeLeftSwipesData, leftSwipesFilters, newSort, true);
      setFilteredLeftData(filtered);
      setLeftCurrentPage(1);
    } else {
      const newSort = { ...rightSwipesSort };
      if (sortOrder) {
        newSort[columnKey] = sortOrder;
      } else {
        delete newSort[columnKey];
      }
      setRightSwipesSort(newSort);
      const filtered = applyFiltersAndSort(activeRightSwipesData, rightSwipesFilters, newSort, false);
      setFilteredRightData(filtered);
      setRightCurrentPage(1);
    }
  };

  // Create filter and sort render functions
  const getUniqueOptions = (data, key) => {
    const values = Array.from(new Set((data || []).map(item => item[key]).filter(Boolean)));
    return values.map(v => ({ label: v, value: v }));
  };

  const buildMakeOptions = (data) => {
    // Extract make from the start of vehicle string, e.g., '2023 Honda Accord' => 'Honda'
    const makes = new Set();
    (data || []).forEach(item => {
      const vehicle = item && item.vehicle;
      if (!vehicle) return;
      const parts = vehicle.split(' ');
      if (parts.length >= 2) {
        makes.add(parts[1]);
      }
    });
    return Array.from(makes).map(m => ({ label: m, value: m.toLowerCase() }));
  };

  const buildColorFamilyOptions = (data) => {
    const families = new Set();
    (data || []).forEach(item => {
      const color = (item && item.color) ? item.color.toLowerCase() : '';
      if (!color) return;
      if (color.includes('white')) families.add('white');
      if (color.includes('black')) families.add('black');
      if (color.includes('red')) families.add('red');
      if (color.includes('blue')) families.add('blue');
      if (color.includes('silver')) families.add('silver');
      if (color.includes('gray') || color.includes('grey')) families.add('gray');
      if (color.includes('green')) families.add('green');
      if (color.includes('pearl')) families.add('pearl');
    });
    return Array.from(families).map(c => ({ label: c.charAt(0).toUpperCase() + c.slice(1), value: c }));
  };

  // Build dynamic filter options for left swipes
  const leftFilterOptions = {
    vin: getUniqueOptions(activeLeftSwipesData, 'vin'),
    stockNo: getUniqueOptions(activeLeftSwipesData, 'stockNo'),
    vehicle: [
      ...buildMakeOptions(activeLeftSwipesData),
      ...getUniqueOptions(activeLeftSwipesData, 'vehicle')
    ],
    condition: [
      { label: 'New (< 500 miles)', value: 'new' },
      { label: 'Used (>= 500 miles)', value: 'used' }
    ],
    color: [
      ...buildColorFamilyOptions(activeLeftSwipesData),
      ...getUniqueOptions(activeLeftSwipesData, 'color')
    ],
  };

  // Build dynamic filter options for right swipes
  const rightFilterOptions = {
    salesRep: getUniqueOptions(activeRightSwipesData, 'salesRep'),
    memberDetails: getUniqueOptions(activeRightSwipesData, 'memberDetails'),
    condition: [ { label: 'New', value: 'New' }, { label: 'Used', value: 'Used' } ],
    vehicle: [
      ...buildMakeOptions(activeRightSwipesData),
      ...getUniqueOptions(activeRightSwipesData, 'vehicle')
    ],
    currentStage: getUniqueOptions(activeRightSwipesData, 'currentStage'),
  };
  const createFilterDropdown = (columnKey, options, isLeftSwipes = true) => {
    return renderFilterDropdown(columnKey, options, isLeftSwipes, 
      isLeftSwipes ? leftSwipesFilters : rightSwipesFilters, 
      handleColumnFilter
    );
  };

  const createSortDropdown = (columnKey, isLeftSwipes = true) => {
    return renderSortDropdown(columnKey, isLeftSwipes, 
      isLeftSwipes ? leftSwipesSort : rightSwipesSort, 
      handleColumnSort
    );
  };

  // Create column definitions with dynamic filter options
  const leftSwipesColumns = createLeftSwipesColumns(
    createFilterDropdown,
    createSortDropdown,
    handleViewDealDetails,
    leftFilterOptions
  );

  const rightSwipesColumns = createRightSwipesColumns(
    createFilterDropdown,
    createSortDropdown,
    handleViewDealDetails,
    rightFilterOptions
  );

  // Swipe gesture handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setSwipeIndicatorActive(false);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    if (activeTab === 'leftSwipes' && touchStart != null) {
      const currentX = e.targetTouches[0].clientX;
      const distance = touchStart - currentX;
      setSwipeIndicatorActive(distance > SWIPE_GESTURE_CONFIG.minSwipeDistance);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > SWIPE_GESTURE_CONFIG.minSwipeDistance;
    const isRightSwipe = distance < -SWIPE_GESTURE_CONFIG.minSwipeDistance;

    if (isLeftSwipe && activeTab === 'leftSwipes') {
      // Swipe left on left swipes table - go to right swipes
      setActiveTab('rightSwipes');
      setFilteredRightData(applyFiltersAndSort(activeRightSwipesData, rightSwipesFilters, rightSwipesSort, false));
      setRightCurrentPage(1);
    } else if (isRightSwipe && activeTab === 'rightSwipes') {
      // Swipe right on right swipes table - go to left swipes
      setActiveTab('leftSwipes');
      setFilteredLeftData(activeLeftSwipesData);
      setLeftCurrentPage(1);
    } else if (isLeftSwipe && activeTab === 'overview') {
      // Swipe left on overview - go to left swipes
      setActiveTab('leftSwipes');
      setFilteredLeftData(activeLeftSwipesData);
      setLeftCurrentPage(1);
    } else if (isRightSwipe && activeTab === 'leftSwipes') {
      // Swipe right on left swipes - go to overview
      setActiveTab('overview');
    }
    setSwipeIndicatorActive(false);
  };

  // Mouse gesture handlers (desktop) — only used on Left Swipes table
  const onMouseDown = (e) => {
    if (activeTab !== 'leftSwipes') return;
    setIsMouseDown(true);
    setTouchEnd(null);
    setTouchStart(e.clientX);
    setSwipeIndicatorActive(false);
  };

  const onMouseMove = (e) => {
    if (!isMouseDown || activeTab !== 'leftSwipes') return;
    setTouchEnd(e.clientX);
    if (touchStart != null) {
      const distance = touchStart - e.clientX;
      setSwipeIndicatorActive(distance > SWIPE_GESTURE_CONFIG.minSwipeDistance);
    }
  };

  const onMouseUp = () => {
    if (!isMouseDown) return;
    setIsMouseDown(false);
    if (!touchStart || touchEnd == null) {
      setSwipeIndicatorActive(false);
      return;
    }
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > SWIPE_GESTURE_CONFIG.minSwipeDistance;
    if (isLeftSwipe && activeTab === 'leftSwipes') {
      setActiveTab('rightSwipes');
      setFilteredRightData(applyFiltersAndSort(activeRightSwipesData, rightSwipesFilters, rightSwipesSort, false));
      setRightCurrentPage(1);
    }
    setSwipeIndicatorActive(false);
  };

  const handlePieClick = (data) => {
    if (data.name === 'Left Swipes') {
      setActiveTab('leftSwipes');
      setFilteredLeftData(activeLeftSwipesData);
      setLeftCurrentPage(1);
    } else if (data.name === 'Right Swipes') {
      setActiveTab('rightSwipes');
      setFilteredRightData(applyFiltersAndSort(activeRightSwipesData, rightSwipesFilters, rightSwipesSort, false));
      setRightCurrentPage(1);
    }
  };

  

  const RIGHT_SWIPE_EXPORT_EXCLUDE_FIELDS = useMemo(() => (
    [
      'fastPassDealerId',
      'fastpassVehicleId',
      'stageHistory',
      'inventoryRowId',
      'vehiclePhoto',
      'memberLastName',
      'loanID',
      'loanId'
    ]
  ), []);

  const buildExportRows = useCallback((rows, excludedKeys = []) => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    if (!excludedKeys || excludedKeys.length === 0) return rows;
    return rows.map((row) => {
      if (!row || typeof row !== 'object') return row;
      const clone = { ...row };
      excludedKeys.forEach((key) => {
        if (key in clone) {
          delete clone[key];
        }
      });
      return clone;
    });
  }, []);

  const handleExport = () => {
    const dataToExport = activeTab === 'leftSwipes' ? filteredLeftData : filteredRightData;
    const sanitizedRows = activeTab === 'rightSwipes'
      ? buildExportRows(dataToExport, RIGHT_SWIPE_EXPORT_EXCLUDE_FIELDS)
      : dataToExport;
    const csvContent = convertToCSV(sanitizedRows);
    downloadCSV(csvContent, `${activeTab}-data.csv`);
    console.log('Exporting filtered data...');
  };

  // Derived paginated data for Left Swipes
  const leftTotal = filteredLeftData.length;
  const leftStartIndex = (leftCurrentPage - 1) * leftPageSize;
  const leftEndIndex = Math.min(leftStartIndex + leftPageSize, leftTotal);
  const pagedLeftData = filteredLeftData.slice(leftStartIndex, leftEndIndex);

  const handleLeftPageChange = (page, pageSize) => {
    setLeftCurrentPage(page);
    if (pageSize !== leftPageSize) setLeftPageSize(pageSize);
  };

  const handleActiveMembersClick = () => {
    // Use backend-provided breakdown if available; otherwise keep existing
    setShowCreditUnionModal(true);
  };

  // Derived paginated data for Right Swipes
  const rightTotal = filteredRightData.length;
  const rightStartIndex = (rightCurrentPage - 1) * rightPageSize;
  const rightEndIndex = Math.min(rightStartIndex + rightPageSize, rightTotal);
  const pagedRightData = filteredRightData.slice(rightStartIndex, rightEndIndex);

  const handleRightPageChange = (page, pageSize) => {
    setRightCurrentPage(page);
    if (pageSize !== rightPageSize) setRightPageSize(pageSize);
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
      <div className="dealer-dashboard-container">
        {activeTab === 'overview' && (
          <OverviewSection 
            leftSwipesData={activeLeftSwipesData}
            rightSwipesData={activeRightSwipesData}
            pieData={pieData}
            handlePieClick={handlePieClick}
            activeMembersCount={activeMembersCount}
            totalOpportunityMillions={totalOpportunityMOverride}
            onActiveMembersClick={handleActiveMembersClick}
            dealerName={toTitleCase(currentDealerhost?.dealer_nm || sellerName)}
          />
        )}
        {activeTab === 'leftSwipes' && (
          <LeftSwipesTable 
            filteredLeftData={pagedLeftData}
            totalLeftCount={leftTotal}
            currentPage={leftCurrentPage}
            pageSize={leftPageSize}
            onPageChange={handleLeftPageChange}
            leftSwipesColumns={leftSwipesColumns}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            swipeIndicatorActive={swipeIndicatorActive}
            handleExport={handleExport}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'rightSwipes' && (
          <RightSwipesTable 
            filteredRightData={pagedRightData}
            totalRightCount={rightTotal}
            currentPage={rightCurrentPage}
            pageSize={rightPageSize}
            onPageChange={handleRightPageChange}
            rightSwipesColumns={rightSwipesColumns}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            handleExport={handleExport}
            setActiveTab={setActiveTab}
          />
        )}
        <DealDetailsModal 
          dealDetailsModal={dealDetailsModal}
          setDealDetailsModal={setDealDetailsModal}
          selectedRecord={selectedRecord}
          fpDealerId={computedFpDealerId}
        />
        <CreditUnionModal 
          open={showCreditUnionModal}
          onClose={() => setShowCreditUnionModal(false)}
          breakdown={creditUnionBreakdown}
          total={activeMembersCountOverride}
        />
      </div>
    </>
  );
};

export default DealerDashboard;
